import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { useToast } from "@/components/Toast";

type Approval = {
  id: number;
  action: string;
  subject_type: string;
  subject_id: number;
  subject_label: string | null;
  payload: Record<string, unknown> | null;
  reason: string | null;
  requested_by: number | null;
  requested_at: string;
  decided_by: number | null;
  decided_at: string | null;
  decision: string | null;
  executed_at: string | null;
  execution_error: string | null;
  expires_at: string | null;
};

type State = { super_admin_count: number; approvals_enabled: boolean };

function expiresIn(iso: string | null): string {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3600000);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export function Approvals() {
  const [rows, setRows] = useState<Approval[] | null>(null);
  const [state, setState] = useState<State | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reasonByAp, setReasonByAp] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const { me } = useAuth();
  const toast = useToast();

  async function load() {
    try {
      const [s, list] = await Promise.all([
        api<State>("/api/admin/approvals/state"),
        api<Approval[]>(`/api/admin/approvals?pending_only=${!showHistory}`),
      ]);
      setState(s);
      setRows(list);
    } catch (e: any) {
      setErr(e?.message ?? "load failed");
    }
  }
  useEffect(() => {
    load();
  }, [showHistory]);

  async function decide(ap: Approval, approve: boolean) {
    setBusy(ap.id);
    try {
      await api(`/api/admin/approvals/${ap.id}/decide`, {
        method: "POST",
        json: { approve, reason: reasonByAp[ap.id] || null },
      });
      toast.success(approve ? "Approved + executed" : "Rejected");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "decide failed");
    } finally {
      setBusy(null);
    }
  }

  const pending = rows?.filter((r) => r.decision === null) ?? [];
  const decided = rows?.filter((r) => r.decision !== null) ?? [];

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl display">Approvals</h1>
          <p className="text-sm text-slate-400">
            Two super-admins required for destructive operations. Today this gate is on{" "}
            <b>delete real key</b>; others can be added by passing a new action string to
            the approval service.
          </p>
        </div>
        <button
          className="btn-secondary text-xs"
          onClick={() => setShowHistory((v) => !v)}
        >
          {showHistory ? "Show pending only" : "Show history"}
        </button>
      </header>

      {state && !state.approvals_enabled && (
        <div className="card bg-amber-500/5 border-amber-500/20 text-amber-200 text-sm">
          <b>Only {state.super_admin_count} super-admin{state.super_admin_count === 1 ? "" : "s"} in the system.</b>{" "}
          4-eyes approval requires at least two. Until a second super-admin is added, destructive
          deletions execute immediately (with a big warning) instead of going through the
          workflow. Add another super-admin in <Link to="/users" className="underline">Users</Link>{" "}
          to enable the gate.
        </div>
      )}

      {err && <div className="card text-rose-300 text-sm">{err}</div>}

      <section className="card">
        <h2 className="font-semibold mb-3">Pending ({pending.length})</h2>
        {pending.length === 0 ? (
          <p className="text-sm text-slate-500">
            No pending requests. A delete-real-key from another super-admin will land here.
          </p>
        ) : (
          <ul className="divide-y divide-white/5">
            {pending.map((ap) => {
              const isMine = ap.requested_by === me?.id;
              return (
                <li key={ap.id} className="py-4 flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm">
                        <span className="badge-gold mr-2">{ap.action}</span>
                        <b>{ap.subject_label ?? `${ap.subject_type}#${ap.subject_id}`}</b>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        requested by user#{ap.requested_by ?? "?"} —{" "}
                        {new Date(ap.requested_at).toLocaleString()} · expires in{" "}
                        {expiresIn(ap.expires_at)}
                      </div>
                      {ap.reason && (
                        <div className="text-xs text-slate-400 mt-1 italic">
                          "{ap.reason}"
                        </div>
                      )}
                      {ap.payload && (
                        <details className="text-[10px] text-slate-500 mt-1">
                          <summary className="cursor-pointer">payload</summary>
                          <pre className="mono bg-black/30 rounded p-2 mt-1">
                            {JSON.stringify(ap.payload, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {isMine ? (
                        <span className="text-xs text-slate-500 italic">
                          you requested this — a different super-admin must decide
                        </span>
                      ) : (
                        <>
                          <button
                            className="btn-secondary text-xs"
                            onClick={() => decide(ap, false)}
                            disabled={busy === ap.id}
                          >
                            Reject
                          </button>
                          <button
                            className="btn-danger text-xs"
                            onClick={() => decide(ap, true)}
                            disabled={busy === ap.id}
                          >
                            {busy === ap.id ? "Working…" : "Approve + execute"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {!isMine && (
                    <input
                      className="input text-xs"
                      placeholder="Optional reason for your decision…"
                      value={reasonByAp[ap.id] ?? ""}
                      onChange={(e) =>
                        setReasonByAp({ ...reasonByAp, [ap.id]: e.target.value })
                      }
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {showHistory && (
        <section className="card">
          <h2 className="font-semibold mb-3">History ({decided.length})</h2>
          {decided.length === 0 ? (
            <p className="text-sm text-slate-500">No history yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="py-2">Action</th>
                  <th>Subject</th>
                  <th>Decision</th>
                  <th>Executed?</th>
                  <th>By</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {decided.map((ap) => (
                  <tr key={ap.id} className="border-t border-white/5">
                    <td className="py-1.5 mono text-xs">{ap.action}</td>
                    <td className="text-slate-400">
                      {ap.subject_label ?? `${ap.subject_type}#${ap.subject_id}`}
                    </td>
                    <td>
                      {ap.decision === "approved" ? (
                        <span className="badge-ok">approved</span>
                      ) : ap.decision === "rejected" ? (
                        <span className="badge-err">rejected</span>
                      ) : (
                        <span className="badge-warn">{ap.decision}</span>
                      )}
                    </td>
                    <td className="text-xs">
                      {ap.execution_error ? (
                        <span className="text-rose-300" title={ap.execution_error}>
                          error
                        </span>
                      ) : ap.executed_at ? (
                        "yes"
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="text-slate-500">
                      req#{ap.requested_by ?? "?"} / dec#{ap.decided_by ?? "?"}
                    </td>
                    <td className="text-slate-500 text-xs">
                      {ap.decided_at
                        ? new Date(ap.decided_at).toLocaleString()
                        : new Date(ap.requested_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
